import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDohServers, saveDohSpeed } from '../api/client'
import { dohQuery } from '../lib/doh'
import { addCustomResolver, readCustomResolvers, removeCustomResolver } from '../lib/customDoh'
import type { CustomResolver } from '../lib/customDoh'
import type { DohServer } from '@shared/api'

export type ProbeStatus = 'pending' | 'ok' | 'failed' | 'unreachable'

export interface Probe {
    provider: string
    url: string
    status: ProbeStatus
    latencyMs?: number
    /** The resolved record, or the resolver's own rcode when it refused. */
    answer?: string
    /** Added by this visitor rather than served from the published list. */
    custom?: boolean
    /** Why an unreachable probe failed, as far as the browser will reveal it.
        'timeout' = no response within the window; 'refused' = the connection
        failed outright. The browser deliberately does not distinguish a network
        block from a CORS rejection, so those are not separated here. */
    reason?: 'timeout' | 'refused'
}

/** Short: a resolver that has not answered in this long is not "slow", it is blocked. */
const PROBE_TIMEOUT_MS = 3000

export function useDohSpeed(hostname: string) {
    const servers = useQuery({
        queryKey: ['doh-servers'],
        queryFn: ({ signal }) => fetchDohServers(signal),
        staleTime: Infinity,
    })

    const [probes, setProbes] = useState<Probe[]>([])
    const [running, setRunning] = useState(false)
    const [custom, setCustom] = useState<CustomResolver[]>(readCustomResolvers)

    const run = useCallback(async (list: (DohServer & { custom?: boolean })[], host: string) => {
        if (list.length === 0) return
        setRunning(true)
        setProbes(list.map((s) => ({
            provider: s.provider, url: s.url, status: 'pending', custom: s.custom,
        })))

        // all at once: they are independent, and serialising would take minutes
        await Promise.all(list.map(async (server) => {
            let result: Probe
            try {
                const res = await dohQuery(server.url, host, 'A', { timeoutMs: PROBE_TIMEOUT_MS })
                result = {
                    provider: server.provider,
                    url: server.url,
                    custom: server.custom,
                    latencyMs: res.latencyMs,
                    // a resolver that answers SERVFAIL is reachable but unhappy;
                    // that is a different fact from being blocked
                    status: res.rcode === 'NOERROR' ? 'ok' : 'failed',
                    answer: res.rcode === 'NOERROR' ? res.answers[0]?.data : res.rcode,
                }
            } catch (err) {
                // an aborted fetch is our own timeout firing; anything else is the
                // connection failing, which the browser will not detail further
                const timedOut = err instanceof DOMException && err.name === 'AbortError'
                result = {
                    provider: server.provider, url: server.url,
                    custom: server.custom, status: 'unreachable',
                    reason: timedOut ? 'timeout' : 'refused',
                }
            }
            setProbes((prev) => prev.map((p) => (p.url === result.url ? result : p)))
        }))

        setRunning(false)
    }, [])

    /** Published list plus this visitor's own, custom entries last. */
    const allServers = useCallback(
        (published: DohServer[]) => [
            ...published,
            ...custom.map((c) => ({ provider: c.provider, url: c.url, custom: true })),
        ],
        [custom],
    )

    useEffect(() => {
        if (servers.data) void run(allServers(servers.data), hostname)
        // deliberately not re-running on every keystroke: the caller triggers it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servers.data, custom])

    // report only what actually resolved; a blocked endpoint says nothing about speed
    useEffect(() => {
        if (running || probes.length === 0) return
        // a visitor's private resolver is not ours to publish
        const measured = probes.filter((p) => p.status === 'ok' && p.latencyMs !== undefined && !p.custom)
        if (measured.length === 0) return
        void saveDohSpeed(measured.map((p) => ({
            name: p.provider, url: p.url, latency: Math.round(p.latencyMs as number),
        }))).catch(() => { /* telemetry, not the feature */ })
    }, [running, probes])

    // persist and update state together, so a custom resolver is saved in the
    // browser the moment it is added and stays until dropCustom removes it
    const addCustom = useCallback((resolver: CustomResolver) => {
        setCustom(addCustomResolver(resolver))
    }, [])

    const dropCustom = useCallback((url: string) => {
        setCustom(removeCustomResolver(url))
    }, [])

    return {
        probes,
        running,
        servers,
        addCustom,
        dropCustom,
        rerun: () => servers.data && run(allServers(servers.data), hostname),
    }
}
